import type { EditorSegment, SummaryPane } from '../data/state.js';
import type { SettingsTab } from './context.js';
import { type SettingsPage, settingsPageFor } from './settings-nav.js';

export type AppRoute =
  | { view: 'demands' }
  | { view: 'demand'; id: string; section: EditorSegment; pane: SummaryPane }
  | { view: 'chat'; id?: string }
  | { view: 'savings' }
  | { view: 'settings'; area: SettingsTab; page: SettingsPage }
  | { view: 'library'; scope: string; tab: 'documents' | 'facts' }
  | { view: 'vendors'; id?: string };

const editorSections: readonly EditorSegment[] = ['info', 'lines', 'quotes', 'files', 'summary', 'decision-summary'];
const summaryPanes: readonly SummaryPane[] = ['overview', 'decision', 'outcomes'];

export function parseAppRoute(search: string): AppRoute {
  const params = new URLSearchParams(search);
  const id = params.get('id') ?? '';
  switch (params.get('view')) {
    case 'demand': {
      if (id === '') return { view: 'demands' };
      const rawSection = params.get('section');
      const section = editorSections.find((value) => value === rawSection) ?? 'lines';
      const rawPane = params.get('pane');
      const pane = summaryPanes.find((value) => value === rawPane) ?? 'overview';
      return { view: 'demand', id, section, pane };
    }
    case 'chat':
      return id === '' ? { view: 'chat' } : { view: 'chat', id };
    case 'savings':
      return { view: 'savings' };
    case 'settings': {
      const rawArea = params.get('area');
      const area: SettingsTab =
        rawArea === 'organization' || rawArea === 'project' || rawArea === 'personal' ? rawArea : 'personal';
      return { view: 'settings', area, page: settingsPageFor(area, params.get('page') ?? undefined) };
    }
    case 'library':
      return {
        view: 'library',
        scope: params.get('scope') ?? '',
        tab: params.get('tab') === 'facts' ? 'facts' : 'documents',
      };
    case 'vendors':
      return id === '' ? { view: 'vendors' } : { view: 'vendors', id };
    default:
      return { view: 'demands' };
  }
}

export function appRouteUrl(route: AppRoute): string {
  if (route.view === 'demands') return '/';
  const params = new URLSearchParams({ view: route.view });
  switch (route.view) {
    case 'demand':
      params.set('id', route.id);
      params.set('section', route.section);
      if (route.section === 'summary') params.set('pane', route.pane);
      break;
    case 'chat':
    case 'vendors':
      if (route.id !== undefined) params.set('id', route.id);
      break;
    case 'settings':
      params.set('area', route.area);
      params.set('page', route.page);
      break;
    case 'library':
      if (route.scope !== '') params.set('scope', route.scope);
      params.set('tab', route.tab);
      break;
    case 'savings':
      break;
  }
  return `/?${params.toString()}`;
}
